import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { History, RotateCcw, X } from 'lucide-react';
import { toast } from '@/components/feedback/toast';
import { cn } from '@/lib/utils';
import { docOf, restoreRevision } from '../api/themes';
import type { ThemeDoc } from '../model/doc';
import { themeKeys, useThemeRevisions } from '../hooks/useThemeDoc';

const KIND_LABEL: Record<string, string> = {
  autosave: 'Autosave',
  manual: 'Checkpoint',
  publish: 'Published',
  restore: 'Restored',
};

function when(iso: string): string {
  const d = new Date(iso);
  const days = Math.round((Date.now() - d.getTime()) / 86400000);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Right-edge overlay listing revision checkpoints, with one-click restore. */
export function StudioHistoryPanel({
  themeId,
  onClose,
  onRestored,
}: {
  themeId: string;
  onClose: () => void;
  onRestored: (doc: ThemeDoc) => void;
}) {
  const qc = useQueryClient();
  const { data: revisions = [], isLoading } = useThemeRevisions(themeId);
  const [busyRev, setBusyRev] = useState<number | null>(null);

  async function restore(rev: number): Promise<void> {
    setBusyRev(rev);
    try {
      const row = await restoreRevision(themeId, rev);
      void qc.invalidateQueries({ queryKey: themeKeys.revisions(themeId) });
      void qc.invalidateQueries({ queryKey: themeKeys.detail(themeId) });
      onRestored(docOf(row));
      toast(`Restored version ${rev}`, 'ok');
    } catch (e) {
      toast((e as Error).message || 'Could not restore', 'error');
    } finally {
      setBusyRev(null);
    }
  }

  return (
    <div className="absolute bottom-3 right-3 top-14 z-30 flex w-72 flex-col overflow-hidden rounded-xl border border-white/12 bg-ink-850 shadow-[0_24px_60px_rgba(0,0,0,0.6)]">
      <div className="flex flex-none items-center justify-between border-b border-white/[0.06] px-4 py-3">
        <span className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.7px] text-lo">
          <History size={13} strokeWidth={2.4} /> Version history
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close history"
          className="grid h-6 w-6 place-items-center rounded text-mid transition-colors hover:bg-white/10 hover:text-hi"
        >
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <p className="px-2 py-3 text-[12px] text-lo">Loading…</p>
        ) : revisions.length === 0 ? (
          <p className="px-2 py-3 text-[12px] leading-relaxed text-lo">
            No checkpoints yet — they're captured automatically as you work, and on every publish.
          </p>
        ) : (
          revisions.map((r) => (
            <div
              key={r.id}
              className="group mb-1 flex items-center gap-2.5 rounded-lg px-2.5 py-2 transition-colors hover:bg-white/5"
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 flex-none rounded-full',
                  r.kind === 'publish' ? 'bg-accent' : 'bg-white/25',
                )}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-bold text-hi">
                  v{r.rev} · {KIND_LABEL[r.kind] ?? r.kind}
                </span>
                <span className="block text-[11px] text-lo">{when(r.created_at)}</span>
              </span>
              <button
                type="button"
                disabled={busyRev != null}
                onClick={() => void restore(r.rev)}
                className="inline-flex flex-none items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[11px] font-bold text-mid opacity-0 transition-all hover:text-hi group-hover:opacity-100 disabled:opacity-30"
              >
                <RotateCcw size={11} />
                {busyRev === r.rev ? '…' : 'Restore'}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
